import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useCallback, useRef } from "react";

interface TrackListen {
  id: string;
  track_id: string;
  user_id: string;
  listen_seconds: number;
  listened_at: string;
  created_at: string;
}

interface TrackListeningStats {
  totalListenTimeSeconds: number;
  totalListenTimeMinutes: number;
  totalTracks: number;
  favoriteTrackId: string | null;
  favoriteTrackListenCount: number;
  recentListens: TrackListen[];
}

/**
 * Hook for tracking and fetching music listening stats
 */
export function useTrackListening() {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  const pendingSecondsRef = useRef<Map<string, number>>(new Map());

  // Fetch all track listening data
  const { data: listens = [], isLoading, refetch } = useQuery({
    queryKey: ['trackListens', userId],
    queryFn: async (): Promise<TrackListen[]> => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('track_listens')
        .select('*')
        .eq('user_id', userId)
        .order('listened_at', { ascending: false })
        .limit(500);

      if (error) {
        console.error('Error fetching track listens:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
  });

  // Record a listening session
  const recordListenMutation = useMutation({
    mutationFn: async ({ trackId, listenSeconds }: { trackId: string; listenSeconds: number }) => {
      if (!userId || listenSeconds < 5) return; // Only record if listened for at least 5 seconds

      const { error } = await supabase
        .from('track_listens')
        .insert({
          track_id: trackId,
          user_id: userId,
          listen_seconds: listenSeconds,
          listened_at: new Date().toISOString(),
        });

      if (error) {
        console.error('Error recording track listen:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trackListens', userId] });
    },
  });

  // Accumulate listening time for batch recording
  const accumulateListenTime = useCallback((trackId: string, seconds: number) => {
    const current = pendingSecondsRef.current.get(trackId) || 0;
    pendingSecondsRef.current.set(trackId, current + seconds);
  }, []);

  // Flush accumulated time to DB
  const flushListenTime = useCallback((trackId: string) => {
    const seconds = pendingSecondsRef.current.get(trackId) || 0;
    if (seconds >= 5) {
      recordListenMutation.mutate({ trackId, listenSeconds: seconds });
    }
    pendingSecondsRef.current.delete(trackId);
  }, [recordListenMutation]);

  // Calculate stats
  const stats: TrackListeningStats = (() => {
    if (!listens.length) {
      return {
        totalListenTimeSeconds: 0,
        totalListenTimeMinutes: 0,
        totalTracks: 0,
        favoriteTrackId: null,
        favoriteTrackListenCount: 0,
        recentListens: [],
      };
    }

    const totalListenTimeSeconds = listens.reduce((sum, l) => sum + l.listen_seconds, 0);
    const uniqueTracks = new Set(listens.map(l => l.track_id));

    // Find favorite track (most listened)
    const trackListenCounts: Record<string, number> = {};
    listens.forEach(l => {
      trackListenCounts[l.track_id] = (trackListenCounts[l.track_id] || 0) + 1;
    });

    let favoriteTrackId: string | null = null;
    let favoriteTrackListenCount = 0;
    Object.entries(trackListenCounts).forEach(([trackId, count]) => {
      if (count > favoriteTrackListenCount) {
        favoriteTrackId = trackId;
        favoriteTrackListenCount = count;
      }
    });

    return {
      totalListenTimeSeconds,
      totalListenTimeMinutes: Math.floor(totalListenTimeSeconds / 60),
      totalTracks: uniqueTracks.size,
      favoriteTrackId,
      favoriteTrackListenCount,
      recentListens: listens.slice(0, 10),
    };
  })();

  return {
    listens,
    stats,
    isLoading,
    refetch,
    recordListen: recordListenMutation.mutate,
    accumulateListenTime,
    flushListenTime,
    isRecording: recordListenMutation.isPending,
  };
}

/**
 * Hook for combined listening stats (audiobooks + music)
 */
export function useCombinedListeningStats() {
  const { session } = useAuth();
  const userId = session?.user?.id;

  const { data, isLoading } = useQuery({
    queryKey: ['combinedListeningStats', userId],
    queryFn: async () => {
      if (!userId) {
        return {
          totalAudiobookMinutes: 0,
          totalMusicMinutes: 0,
          totalListeningMinutes: 0,
          favoriteAudiobook: null as { id: number; title: string; listens: number } | null,
          mostRepeats: 0,
          likedTracksCount: 0,
          likedAudiobooksCount: 0,
        };
      }

      // Fetch audiobook sessions
      const { data: audiobookSessions } = await supabase
        .from('audiobook_sessions')
        .select('book_id, total_listen_time_seconds')
        .eq('user_id', userId);

      // Fetch music track listens
      const { data: trackListens } = await supabase
        .from('track_listens')
        .select('track_id, listen_seconds')
        .eq('user_id', userId);

      // Fetch liked counts
      const { count: likedTracksCount } = await supabase
        .from('liked_tracks')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      const { count: likedAudiobooksCount } = await supabase
        .from('liked_audiobooks')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      // Fetch book play counts for most replayed
      const { data: playCounts } = await supabase
        .from('book_play_counts')
        .select('book_id, play_count')
        .order('play_count', { ascending: false })
        .limit(1);

      // Calculate audiobook stats
      const totalAudiobookSeconds = (audiobookSessions || []).reduce(
        (sum, s) => sum + (s.total_listen_time_seconds || 0), 0
      );
      const totalAudiobookMinutes = Math.floor(totalAudiobookSeconds / 60);

      // Calculate music stats
      const totalMusicSeconds = (trackListens || []).reduce(
        (sum, t) => sum + (t.listen_seconds || 0), 0
      );
      const totalMusicMinutes = Math.floor(totalMusicSeconds / 60);

      // Find favorite audiobook (most listened)
      const audiobookListenCounts: Record<number, number> = {};
      (audiobookSessions || []).forEach(s => {
        audiobookListenCounts[s.book_id] = (audiobookListenCounts[s.book_id] || 0) + s.total_listen_time_seconds;
      });

      let favoriteAudiobookId: number | null = null;
      let maxListenTime = 0;
      Object.entries(audiobookListenCounts).forEach(([bookId, time]) => {
        if (time > maxListenTime) {
          favoriteAudiobookId = parseInt(bookId);
          maxListenTime = time;
        }
      });

      // Fetch favorite audiobook title
      let favoriteAudiobook: { id: number; title: string; listens: number } | null = null;
      if (favoriteAudiobookId) {
        const { data: book } = await supabase
          .from('books')
          .select('id, title')
          .eq('id', favoriteAudiobookId)
          .single();

        if (book) {
          favoriteAudiobook = {
            id: book.id,
            title: book.title,
            listens: Math.ceil(maxListenTime / 60), // minutes listened
          };
        }
      }

      const mostRepeats = playCounts?.[0]?.play_count || 0;

      return {
        totalAudiobookMinutes,
        totalMusicMinutes,
        totalListeningMinutes: totalAudiobookMinutes + totalMusicMinutes,
        favoriteAudiobook,
        mostRepeats,
        likedTracksCount: likedTracksCount || 0,
        likedAudiobooksCount: likedAudiobooksCount || 0,
      };
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
  });

  return {
    data: data || {
      totalAudiobookMinutes: 0,
      totalMusicMinutes: 0,
      totalListeningMinutes: 0,
      favoriteAudiobook: null,
      mostRepeats: 0,
      likedTracksCount: 0,
      likedAudiobooksCount: 0,
    },
    isLoading,
  };
}
